import { useState } from "react";
import useCodificacion from "../../../../hooks/masters/useCodificacion";
import {
  Page,
  Left,
  Right,
  HeaderRow,
  RoleListContainer,
  RoleItem,
  SearchInput,
} from "../styles/component.style";
import { BtnGeneral } from "../../../../styles/Buttons";
import SearchableSelect from "../../../../components/Common/SearchableSelect";

/* ─── Acordeón (estilo idéntico a Users.js) ─── */
function Accordion({ title, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: "6px", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "5px 10px",
          background: open ? "#f1f5f9" : "#f8fafc",
          border: "none",
          borderRadius: open ? "6px 6px 0 0" : "6px",
          cursor: "pointer",
          fontSize: "11px",
          fontWeight: "600",
          color: "#1e40af",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {title}
          {count != null && (
            <span
              style={{
                background: "#dbeafe",
                color: "#2563eb",
                borderRadius: "10px",
                padding: "0 6px",
                fontSize: "10px",
                fontWeight: "700",
              }}
            >
              {count}
            </span>
          )}
        </span>
        <span style={{ fontSize: "10px", color: "#94a3b8" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ padding: "6px 8px", borderTop: "1px solid #e2e8f0" }}>{children}</div>}
    </div>
  );
}

/* ─── Field: label arriba + input abajo ─── */
function Field({ lbl, required, children, fullWidth = false }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "3px",
        gridColumn: fullWidth ? "1 / -1" : "auto",
      }}
    >
      <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
        {lbl}
        {required && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

export default function Codificacion() {
  const {
    codificaciones,
    consecutivos,
    selectedId,
    form,
    setForm,
    opciones,
    newOpcion,
    setNewOpcion,
    search,
    setSearch,
    loading,
    saving,
    savingOpcion,
    handleSelect,
    handleNew,
    handleSave,
    handleDelete,
    handleAddOpcion,
    handleDeleteOpcion,
  } = useCodificacion();

  const handleConsecutivoChange = (val) => {
    const selectedCons = consecutivos.find(
      (c) => String(c.Consecutivo ?? c.id) === String(val)
    );
    setForm((prev) => ({
      ...prev,
      consecutivo: val,
      API: prev.API ? prev.API : selectedCons?.Api || "",
    }));
  };

  return (
    <Page>
      {/* ══ PANEL IZQUIERDO ══ */}
      <Left>
        <HeaderRow>
          <h3 style={{ margin: 0, fontSize: "13px" }}>Codificación</h3>
          <BtnGeneral
            bg="cyan"
            padding="4px 8px"
            radius="5px"
            fw="600"
            fs="11px"
            onClick={handleNew}
          >
            Nuevo
          </BtnGeneral>
        </HeaderRow>

        <SearchInput
          placeholder="Buscar x código, API..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <RoleListContainer>
          {loading ? (
            <div style={{ padding: "12px", color: "#94a3b8", textAlign: "center", fontSize: "11px" }}>
              Cargando codificaciones...
            </div>
          ) : codificaciones.length > 0 ? (
            codificaciones.map((c) => (
              <RoleItem
                key={c.id}
                active={selectedId === c.id}
                onClick={() => handleSelect(c.id)}
              >
                <div>
                  <strong style={{ fontSize: "12px" }}>
                    {c.consecutivo} - {c.API || c.ConsecutivoNombre || "Sin descripción"}
                  </strong>
                  <div style={{ fontSize: "10px", color: "#64748b", marginTop: "2px" }}>
                    Cod API: <strong>{c.cod_API ?? "-"}</strong> | Opciones:{" "}
                    <strong>{c.total_opciones ?? 0}</strong>
                    {c.unidades ? ` | ${c.unidades}` : ""}
                  </div>
                </div>
              </RoleItem>
            ))
          ) : (
            <div style={{ padding: "12px", color: "#94a3b8", textAlign: "center", fontSize: "11px" }}>
              {search ? "No se encontraron codificaciones" : "No hay codificaciones registradas"}
            </div>
          )}
        </RoleListContainer>
      </Left>

      {/* ══ PANEL DERECHO ══ */}
      <Right style={{ overflowY: "auto", padding: "8px 10px" }}>
        <HeaderRow style={{ marginBottom: "6px" }}>
          <span style={{ fontSize: "11px", fontWeight: "600", color: "#334155" }}>
            {form.id
              ? `Editor: Consecutivo ${form.consecutivo} - ${form.API || "Codificación"}`
              : "Nueva Codificación"}
          </span>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            {form.id && (
              <BtnGeneral
                bg="red"
                padding="4px 12px"
                radius="5px"
                fw="600"
                fs="11px"
                onClick={() => handleDelete(form.id)}
              >
                Eliminar
              </BtnGeneral>
            )}
            <BtnGeneral
              bg="green"
              padding="4px 16px"
              radius="5px"
              fw="600"
              fs="11px"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? "Guardando..." : "Guardar"}
            </BtnGeneral>
          </div>
        </HeaderRow>

        {/* ── Sección: Parámetros Generales de Codificación ── */}
        <Accordion title="Parámetros Generales de Codificación" defaultOpen={true}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
            <Field lbl="ID:">
              <input
                style={{ ...inp, background: "#f8fafc", color: "#94a3b8" }}
                readOnly
                value={form.id || ""}
                placeholder="(Asignado automáticamente)"
              />
            </Field>

            <Field lbl="Consecutivo (Actividad / Prueba):" required>
              <SearchableSelect
                key={`select-cons-${form.id || "new"}`}
                value={form.consecutivo ? String(form.consecutivo) : ""}
                onValueChange={handleConsecutivoChange}
                options={consecutivos}
                getOptionValue={(c) => String(c.Consecutivo ?? c.id)}
                getOptionLabel={(c) => `${c.Consecutivo} - ${c.Api || "Sin descripción"}`}
                placeholder="Seleccione consecutivo..."
                multiple={false}
                searchable={true}
                sx={selectSx}
              />
            </Field>

            <Field lbl="Código API (cod_API):">
              <input
                type="number"
                style={inp}
                value={form.cod_API}
                placeholder="Ej: 77"
                onChange={(e) => setForm({ ...form, cod_API: e.target.value })}
              />
            </Field>

            <Field lbl="Unidades de Medida:">
              <input
                style={inp}
                value={form.unidades}
                placeholder="Ej: mg/dL, dosis, años..."
                onChange={(e) => setForm({ ...form, unidades: e.target.value })}
              />
            </Field>

            <Field lbl="Nombre / Descripción de la API:" fullWidth>
              <input
                style={inp}
                value={form.API}
                placeholder="Ej: Suministro de hierro en la primera Infancia"
                onChange={(e) => setForm({ ...form, API: e.target.value })}
              />
            </Field>
          </div>
        </Accordion>

        {/* ── Sección: Opciones de Respuesta / Lista de Valores ── */}
        {form.id ? (
          <Accordion
            title="Opciones de Respuesta / Lista de Valores (ValorCodificacion)"
            count={opciones.length}
            defaultOpen={true}
          >
            {/* Fila para agregar nueva opción */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "90px 1fr 80px auto",
                gap: "8px",
                alignItems: "flex-end",
                marginBottom: "10px",
                padding: "8px 10px",
                background: "#f8fafc",
                borderRadius: "5px",
                border: "1px solid #e2e8f0",
              }}
            >
              <Field lbl="IdLista (Código):" required>
                <input
                  type="number"
                  style={inp}
                  value={newOpcion.IdLista}
                  placeholder="Ej: 1"
                  onChange={(e) => setNewOpcion({ ...newOpcion, IdLista: e.target.value })}
                />
              </Field>

              <Field lbl="Descripción de la Opción (Valor):" required>
                <input
                  style={inp}
                  value={newOpcion.Valor}
                  placeholder="Ej: Sospecha de deterioro cognoscitivo"
                  onChange={(e) => setNewOpcion({ ...newOpcion, Valor: e.target.value })}
                />
              </Field>

              <Field lbl="Orden:">
                <input
                  type="number"
                  style={inp}
                  value={newOpcion.orden}
                  placeholder="1"
                  onChange={(e) => setNewOpcion({ ...newOpcion, orden: e.target.value })}
                />
              </Field>

              <div>
                <BtnGeneral
                  bg="teal"
                  padding="4px 12px"
                  radius="4px"
                  fs="11px"
                  fw="600"
                  disabled={savingOpcion}
                  onClick={handleAddOpcion}
                >
                  {savingOpcion ? "Agregando..." : "+ Agregar"}
                </BtnGeneral>
              </div>
            </div>

            {/* Tabla de opciones */}
            {opciones.length === 0 ? (
              <p
                style={{
                  color: "#94a3b8",
                  fontSize: "11px",
                  textAlign: "center",
                  padding: "14px 0",
                  margin: 0,
                }}
              >
                Sin opciones configuradas para esta codificación. Use el formulario superior para
                agregar.
              </p>
            ) : (
              <table style={tbl}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ ...th, width: "80px" }}>IdLista</th>
                    <th style={th}>Descripción del Valor</th>
                    <th style={{ ...th, width: "60px", textAlign: "center" }}>Orden</th>
                    <th style={{ ...th, textAlign: "center", width: "52px" }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {opciones.map((opt) => {
                    const optId = opt.Id ?? opt.id;
                    return (
                      <tr key={optId} style={{ borderTop: "1px solid #f1f5f9" }}>
                        <td style={td}>
                          <span
                            style={{
                              background: "#e0f2fe",
                              color: "#0369a1",
                              fontWeight: "700",
                              fontSize: "11px",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              display: "inline-block",
                            }}
                          >
                            {opt.IdLista}
                          </span>
                        </td>
                        <td style={td}>
                          <strong style={{ fontSize: "11px", color: "#1e293b" }}>
                            {opt.Valor}
                          </strong>
                        </td>
                        <td style={{ ...td, textAlign: "center", color: "#64748b" }}>
                          {opt.orden ?? "-"}
                        </td>
                        <td style={{ ...td, textAlign: "center" }}>
                          <BtnGeneral
                            bg="red"
                            padding="2px 6px"
                            radius="4px"
                            fs="10px"
                            onClick={() => handleDeleteOpcion(optId)}
                            title="Eliminar opción"
                          >
                            ✕
                          </BtnGeneral>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Accordion>
        ) : (
          <div
            style={{
              padding: "16px",
              background: "#f8fafc",
              border: "1px dashed #cbd5e1",
              borderRadius: "6px",
              textAlign: "center",
              color: "#64748b",
              fontSize: "11px",
              marginTop: "8px",
            }}
          >
            Guarde la cabecera de la Codificación para habilitar la parametrización de sus opciones.
          </div>
        )}
      </Right>
    </Page>
  );
}

/* ─── Estilos idénticos a Users.js ─── */
const inp = {
  width: "100%",
  padding: "3px 7px",
  borderRadius: "4px",
  border: "1px solid #cbd5e1",
  fontSize: "11px",
  boxSizing: "border-box",
  outline: "none",
  marginBottom: 0,
};

const tbl = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "11px",
};

const th = {
  padding: "4px 8px",
  fontWeight: "600",
  color: "#64748b",
  textAlign: "left",
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const td = {
  padding: "5px 8px",
  verticalAlign: "middle",
};

const selectSx = {
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    height: "26px",
    minHeight: "26px",
    borderRadius: "4px",
    fontSize: "11px",
    fontFamily: "var(--font-body)",
    backgroundColor: "#ffffff",
    "& fieldset": {
      borderColor: "#cbd5e1",
    },
    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3b82f6",
    },
  },
  "& .MuiSelect-select": {
    padding: "2px 8px !important",
    display: "flex",
    alignItems: "center",
    fontSize: "11px",
    fontFamily: "var(--font-body)",
  },
};

